import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { UX2, Maniless } from '@wsb/guac-widget-core';
import dataAids from '../constants/dataAids';
import renderModes from '../constants/renderModes';
import * as MetricsEvents from '../constants/metrics';
import routes from '../../constants/routes';
import { trackC1 } from '../utils/trackMetric';
import { HIVEMIND_ON } from '../../constants/hivemind';
import SuggestedServices from './SuggestedServices';
import { setSelectedService } from '../actions/ServiceList';
import { OLA_AUTO_ADD_SERVICES } from '../../constants/experiments';
import { isExperimentEnabled } from '../utils/hiveMind';
import ConfigStore from '../stores/Config';
import { useStore } from '../hooks/useStore';

const styles = {
  modalBackground: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'transparent',
    paddingHorizontal: 'small'
  },
  text: {
    fontFamily: 'gdsherpa, Helvetica, Arial, sans-serif',
    fontSize: '16px',
    lineHeight: '20px',
    color: 'white',
    textAlign: 'left',
    marginBottom: 0
  },
  learnMoreContainer: {
    marginBottom: '18px'
  },
  learnMore: {
    color: '#61EDEA !important',
    fontFamily: 'gdsherpa, Helvetica, Arial, sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 850,
    lineHeight: '20px'
  },
  benefitsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: 12,
    marginBottom: '28px',
    '@md': {
      gridTemplateColumns: '1fr 1fr',
      width: '600px'
    }
  },
  item: {
    display: 'flex',
    flexDirection: 'row'
  },
  icon: {
    flexShrink: 0,
    marginRight: '14px'
  }
};

const EmptyServiceListModal = ({
  widgetId,
  ctaUrl,
  staticContent,
  olaBenefitsZeroStateExperiment,
  isAddServicesMutatorExperiment,
  experimentReleaseDate,
  websiteCreatedDate
}) => {
  const {
    Element: { Block },
    Component: { ZeroStateOverlay }
  } = UX2;
  const {
    context: { Router }
  } = Maniless;

  // Get configuration state to check if user is website owner
  const configState = useStore(ConfigStore);
  const { isWebsiteOwner } = configState;

  const isOlaAutoAddServicesEnabled = isExperimentEnabled.bind(
    this,
    ConfigStore,
    OLA_AUTO_ADD_SERVICES
  );

  useEffect(() => {
    trackC1(
      olaBenefitsZeroStateExperiment === HIVEMIND_ON
        ? MetricsEvents.APPOINTMENTS_ZERO_STATE_BENEFITS_VARIANT_IMPRESSION
        : MetricsEvents.APPOINTMENTS_ZERO_STATE_BENEFITS_CONTROL_IMPRESSION
    );
    if (isAddServicesMutatorExperiment && websiteCreatedDate >= experimentReleaseDate) {
      trackC1(MetricsEvents.SERVICE_DETAILS_VARIANT_IMPRESSION);
    } else {
      trackC1(MetricsEvents.SERVICE_DETAILS_CONTROL_IMPRESSION);
    }
  }, [olaBenefitsZeroStateExperiment]);

  const openUrl = (event, router) => {
    setSelectedService({});
    event.stopPropagation();
    trackC1(MetricsEvents.APPOINTMENTS_ZERO_STATE_ADD_SERVICES_CTA_CLICKED);

    if (isAddServicesMutatorExperiment && websiteCreatedDate >= experimentReleaseDate) {
      router.updateRoute({ widget: widgetId, route: '/' });
      setTimeout(() => {
        router.updateRoute({ widget: widgetId, route: routes.SERVICES_DETAILS });
      }, 0);
    } else {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Router.Consumer>
      { router => (
        <Block style={ styles.modalBackground } data-aid={ dataAids.EMPTY_CONTAINER_RENDERED }>
          <ZeroStateOverlay
            renderMode={ renderModes.EDIT }
            title={ staticContent.emptyServiceListTitle }
            description={
              isOlaAutoAddServicesEnabled() ? (
                <SuggestedServices
                  router={ router }
                  widgetId={ widgetId }
                  staticContent={ staticContent }
                />
              ) : (
                staticContent.emptyServiceListDescription
              )
            }
            button={{
              onClick: event => openUrl(event, router),
              children: staticContent.emptyServiceManuallyCallToAction || 'Add Service Manually',
              // Disable the button if the user is not a website owner
              disabled: !isWebsiteOwner
            }}
          />
        </Block>
      ) }
    </Router.Consumer>
  );
};

EmptyServiceListModal.propTypes = {
  ctaUrl: PropTypes.string,
  category: PropTypes.string,
  staticContent: PropTypes.object.isRequired,
  isAddServicesMutatorExperiment: PropTypes.bool,
  experimentReleaseDate: PropTypes.string,
  websiteCreatedDate: PropTypes.string,
  widgetId: PropTypes.string
};

export default EmptyServiceListModal;
